import { useState, useEffect, useRef, useCallback } from 'react';

export function useUnsavedChanges(data, onSave) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [saving, setSaving] = useState(false);
  const initialDataRef = useRef(null);

  // Initialize or update the baseline data
  useEffect(() => {
    if (initialDataRef.current === null) {
      initialDataRef.current = JSON.stringify(data);
      setHasUnsavedChanges(false);
    } else {
      const currentData = JSON.stringify(data);
      const hasChanges = currentData !== initialDataRef.current;
      setHasUnsavedChanges(hasChanges);
    }
  }, [data]);

  const handleNavigation = useCallback((navigationFn) => {
    if (hasUnsavedChanges && !saving) {
      setPendingNavigation(() => navigationFn);
      setShowUnsavedModal(true);
    } else {
      navigationFn();
    }
  }, [hasUnsavedChanges, saving]);

  const handleSaveAndNavigate = useCallback(async () => {
    if (!onSave) {
      setShowUnsavedModal(false);
      if (pendingNavigation) {
        pendingNavigation();
      }
      return;
    }

    setSaving(true);
    try {
      await onSave(data);
      setHasUnsavedChanges(false);
      setShowUnsavedModal(false);
      initialDataRef.current = JSON.stringify(data);
      
      if (pendingNavigation) {
        pendingNavigation();
      }
    } catch (error) {
      console.error('Error saving:', error);
      throw error; // Re-throw to let the component handle it
    } finally {
      setSaving(false);
      setPendingNavigation(null);
    }
  }, [data, onSave, pendingNavigation]);

  const handleNavigateWithoutSaving = useCallback(() => {
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    setSaving(false);
    
    if (pendingNavigation) {
      pendingNavigation();
    }
    setPendingNavigation(null);
  }, [pendingNavigation]);

  const handleCancelNavigation = useCallback(() => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
    setSaving(false);
  }, []);

  const markAsSaved = useCallback(() => {
    initialDataRef.current = JSON.stringify(data);
    setHasUnsavedChanges(false);
    setSaving(false);
  }, [data]);

  const resetUnsavedChanges = useCallback(() => {
    initialDataRef.current = null;
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    setPendingNavigation(null);
    setSaving(false);
  }, []);

  return {
    hasUnsavedChanges,
    showUnsavedModal,
    saving,
    handleNavigation,
    handleSaveAndNavigate,
    handleNavigateWithoutSaving,
    handleCancelNavigation,
    markAsSaved,
    resetUnsavedChanges
  };
}

// Default export for flexibility
export default useUnsavedChanges;